import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { projectPortfolioApi, managementProductsQueryKey } from '@/lib/api/project-portfolio';

type SnapshotLesson = { code?: string; whatHappened?: string };

function snapshotLessons(snapshot: Record<string, unknown>): SnapshotLesson[] {
  const raw = snapshot.lessons;
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is SnapshotLesson => item != null && typeof item === 'object');
}

export function ProjectManagementProductsCard({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const { data: products = [] } = useQuery({
    queryKey: managementProductsQueryKey(projectId),
    queryFn: () => projectPortfolioApi.listProducts(projectId),
    retry: false,
  });

  const download = async (productId: string, type: string, version: number) => {
    const blob = await projectPortfolioApi.downloadProductPdf(projectId, productId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type.toLowerCase()}-v${version}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('workspace.products.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {products.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('workspace.products.empty')}</p>
        ) : (
          <ul className="space-y-4">
            {products.map((product) => {
              const lessons = snapshotLessons(product.snapshot);
              return (
                <li key={product.id} className="space-y-2 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <Badge variant="secondary">{t(`workspace.products.type.${product.type}`)}</Badge>
                      <span className="ml-2 text-muted-foreground">
                        {t('workspace.products.version', { version: product.version })} ·{' '}
                        {new Date(product.createdAt).toLocaleString(i18n.language)}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void download(product.id, product.type, product.version)}
                    >
                      {t('workspace.products.download')}
                    </Button>
                  </div>
                  {lessons.length > 0 && (
                    <div className="rounded-md border bg-muted/30 p-2">
                      <p className="mb-1 text-xs font-medium text-muted-foreground">
                        {t('workspace.products.lessonsInSnapshot', { count: lessons.length })}
                      </p>
                      <ul className="list-inside list-disc space-y-0.5 text-xs">
                        {lessons.slice(0, 5).map((lesson, idx) => (
                          <li key={`${product.id}-lesson-${idx}`}>
                            {lesson.code ? `${lesson.code}: ` : ''}
                            {lesson.whatHappened ?? '—'}
                          </li>
                        ))}
                        {lessons.length > 5 && (
                          <li className="list-none text-muted-foreground">
                            {t('workspace.products.lessonsTruncated', { count: lessons.length - 5 })}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
